import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Target, Briefcase, FileCheck, Calendar, MessageSquare, TrendingUp, Shield, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import MainNavigation from '../components/layout/MainNavigation';
import Footer from '../components/layout/Footer';

const ForOrganizations = () => {
  const navigate = useNavigate();

  const challenges = [
    { text: "Struggling to find qualified trainers for specialized topics?" },
    { text: "Wasting time coordinating training logistics?" },
    { text: "No centralized system to manage trainer relationships?" },
    { text: "Difficulty tracking training ROI and outcomes?" }
  ];

  const solutions = [
    {
      title: "Discover Expert Trainers Instantly",
      description: "Access 18,000+ verified training professionals across 75+ locations. Filter by expertise, industry, language, and ratings.",
      icon: Target,
      stat: "18,000+",
      statLabel: "Verified Trainers"
    },
    {
      title: "Streamline Proposal Management",
      description: "Post requirements once, receive proposals from qualified trainers, compare side-by-side, and decide faster.",
      icon: FileCheck,
      stat: "3x Faster",
      statLabel: "Decision Making"
    },
    {
      title: "Coordinate Training Seamlessly",
      description: "Calendar management, venue coordination, participant tracking, and automated reminders—all in one place.",
      icon: Calendar,
      stat: "80%",
      statLabel: "Time Saved"
    },
    {
      title: "Direct Communication Hub",
      description: "No more email chains. Message trainers directly, share documents, and get instant responses.",
      icon: MessageSquare,
      stat: "Real-time",
      statLabel: "Collaboration"
    },
    {
      title: "Financial Process Automation",
      description: "Generate POs, track invoices, manage payments, and maintain complete audit trails automatically.",
      icon: Briefcase,
      stat: "100%",
      statLabel: "Audit Trail"
    },
    {
      title: "Data-Driven Insights",
      description: "Track training impact, trainer performance, budget utilization, and ROI with comprehensive analytics.",
      icon: TrendingUp,
      stat: "360°",
      statLabel: "Visibility"
    }
  ];

  const testimonials = [
    {
      name: "Sneha S",
      role: "Training Manager",
      company: "Flipkart",
      logo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=200&h=200&fit=crop",
      quote: "Our experiences have been seamless. We fulfill most of our training needs through this platform. The database of trainers and venues is exceptional.",
      metric: "200+",
      metricLabel: "Sessions Completed"
    },
    {
      name: "Mona K",
      role: "HR Manager",
      company: "Heidelberg Cement",
      logo: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200&h=200&fit=crop",
      quote: "Partners for 3 years in technical training. Excellent Pan-India trainers who can facilitate in many regional languages. More experiential than theoretical.",
      metric: "95%",
      metricLabel: "Satisfaction Rate"
    },
    {
      name: "Vivek Mehrotra",
      role: "Training Head",
      company: "Janalakshmi Financial Services",
      logo: "https://images.unsplash.com/photo-1554774853-719586f82d77?w=200&h=200&fit=crop",
      quote: "A pleasure working during our ramp-up across India. Much-needed support in identifying suitable venues that met our requirements.",
      metric: "50+",
      metricLabel: "Locations Covered"
    }
  ];

  const features = [
    { icon: Shield, text: "Enterprise-grade security" },
    { icon: CheckCircle, text: "Verified trainer profiles" },
    { icon: TrendingUp, text: "Real-time analytics" },
    { icon: Users, text: "Dedicated account support" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <MainNavigation />

      {/* Hero Section - Professional & Clean */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 to-white"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center bg-indigo-100 rounded-full px-4 py-2 mb-6">
                <Briefcase className="w-4 h-4 text-indigo-600 mr-2" />
                <span className="text-sm font-semibold text-indigo-900">For Organizations</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                Corporate Training,
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Simplified
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                From finding the right trainer to tracking training outcomes—manage your entire corporate training ecosystem in one powerful platform.
              </p>

              <div className="space-y-3 mb-10">
                {challenges.map((challenge, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{challenge.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl transition-all px-8"
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-indigo-600"
                >
                  Talk to Sales
                </Button>
              </div>

              <div className="mt-8 grid grid-cols-4 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                      <div className="text-xs text-gray-600">{feature.text}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              {/* Floating UI mockup or illustration placeholder */}
              <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8 shadow-2xl">
                <div className="bg-white rounded-2xl p-6 shadow-lg mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full"></div>
                      <div>
                        <div className="h-3 w-24 bg-gray-200 rounded"></div>
                        <div className="h-2 w-16 bg-gray-100 rounded mt-2"></div>
                      </div>
                    </div>
                    <div className="h-8 w-20 bg-green-100 rounded-lg"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-100 rounded"></div>
                    <div className="h-2 bg-gray-100 rounded w-4/5"></div>
                    <div className="h-2 bg-gray-100 rounded w-3/5"></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow">
                    <div className="h-8 w-8 bg-blue-100 rounded-lg mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow">
                    <div className="h-8 w-8 bg-purple-100 rounded-lg mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section - Card Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              One Platform, Complete Solution
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything your L&D team needs to execute flawless training programs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Card 
                  key={index}
                  className="group border-none shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center group-hover:bg-indigo-600 transition-colors">
                        <Icon className="w-7 h-7 text-indigo-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-indigo-600">{solution.stat}</div>
                        <div className="text-xs text-gray-500">{solution.statLabel}</div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{solution.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials - Enterprise Style */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Leading Organizations
            </h2>
            <p className="text-xl text-gray-600">See how companies transform their training operations</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <img 
                      src={testimonial.logo}
                      alt={testimonial.company}
                      className="w-16 h-16 rounded-lg object-cover mb-4"
                    />
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-3xl font-bold text-indigo-600">{testimonial.metric}</div>
                        <div className="text-sm text-gray-500">{testimonial.metricLabel}</div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
                  
                  <div className="border-t pt-4">
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm font-semibold text-indigo-600 mt-1">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">18,000+</div>
              <div className="text-indigo-200">Verified Trainers</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">~100</div>
              <div className="text-indigo-200">Organizations</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">60,000+</div>
              <div className="text-indigo-200">Training Mandays</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">~75</div>
              <div className="text-indigo-200">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Training Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join leading organizations who've streamlined their corporate training with our platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-indigo-600 hover:bg-indigo-700 shadow-lg px-12"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            >
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForOrganizations;
